import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LogOut, Store, Package, MapPin, Phone, User,
  Calendar, DollarSign, Navigation, CheckCircle,
  Clock, XCircle, Loader
} from 'lucide-react';
import { toast } from 'sonner';
import { playNotificationSound } from '../../utils/notificationSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pendiente', color: '#FFA500', icon: Clock },
  preparing: { label: 'Preparando', color: '#3B82F6', icon: Package },
  delivered: { label: 'Entregado', color: '#22C55E', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: '#EF4444', icon: XCircle }
};

const OrdersManagement = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, logout, getAdminBasePath } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const prevOrderCountRef = React.useRef(0);

  useEffect(() => {
    if (!user) {
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
      return;
    }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    loadOrders();
    const interval = setInterval(loadOrders, 15000);
    return () => clearInterval(interval);
  }, [user, navigate, filterStatus, storeDomain, adminPath]);

  const loadOrders = async () => {
    try {
      const url = filterStatus ? `${API}/orders?status=${filterStatus}` : `${API}/orders`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const newOrders = response.data;
      if (newOrders.length > prevOrderCountRef.current && prevOrderCountRef.current > 0) {
        playNotificationSound('order');
      }
      prevOrderCountRef.current = newOrders.length;
      setOrders(newOrders);
    } catch (error) {
      toast.error('Error al cargar pedidos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      toast.success('Estado actualizado');
      loadOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleLogout = () => {
    logout();
    navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
    toast.success('Sesión cerrada');
  };

  const openMap = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size={48} className="animate-spin text-accent-text" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Store className="text-accent-text flex-shrink-0" size={24} />
              <div className="min-w-0">
                <h1 className="text-base md:text-lg font-bold truncate">Gestión de Pedidos</h1>
                <p className="text-xs text-text-muted truncate">{user?.username}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => navigate(`${basePath}/dashboard`)} className="btn-secondary text-sm">
                Productos
              </button>
              <button onClick={handleLogout} className="btn-secondary text-sm">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-4 md:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-8">
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <p className="text-sm text-text-muted mb-1">Total Pedidos</p>
            <p className="text-3xl font-bold text-accent-text">{orders.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <p className="text-sm text-text-muted mb-1">Pendientes</p>
            <p className="text-3xl font-bold" style={{ color: '#FFA500' }}>
              {orders.filter(o => o.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <p className="text-sm text-text-muted mb-1">En Preparación</p>
            <p className="text-3xl font-bold" style={{ color: '#3B82F6' }}>
              {orders.filter(o => o.status === 'preparing').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <p className="text-sm text-text-muted mb-1">Entregados</p>
            <p className="text-3xl font-bold" style={{ color: '#22C55E' }}>
              {orders.filter(o => o.status === 'delivered').length}
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white p-4 rounded-xl border mb-6" style={{ borderColor: 'var(--border-light)' }}>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setFilterStatus('')}
              className={`px-4 py-2 rounded-lg border transition-all ${
                filterStatus === '' ? 'border-accent-primary bg-accent-wash' : 'border-border-light'
              }`}
            >
              Todos ({orders.length})
            </button>
            {Object.entries(statusConfig).map(([status, config]) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg border transition-all ${
                  filterStatus === status ? 'border-accent-primary bg-accent-wash' : 'border-border-light'
                }`}
              >
                {config.label} ({orders.filter(o => o.status === status).length})
              </button>
            ))}
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <Package size={64} className="mx-auto mb-4 text-text-muted" />
              <p className="text-xl text-text-muted">No hay pedidos</p>
            </div>
          ) : (
            orders.map((order) => {
              const StatusIcon = statusConfig[order.status].icon;
              const statusColor = statusConfig[order.status].color;

              return (
                <div
                  key={order.id}
                  className="bg-white rounded-xl border p-6 hover:shadow-md transition-shadow"
                  style={{ borderColor: 'var(--border-light)' }}
                >
                  <div className="flex flex-wrap gap-6">
                    {/* Order Info */}
                    <div className="flex-1 min-w-[300px]">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-bold text-lg text-text-primary flex items-center gap-2">
                            <User size={20} />
                            {order.customer_name}
                          </h3>
                          <p className="text-sm text-text-muted flex items-center gap-2 mt-1">
                            <Calendar size={16} />
                            {new Date(typeof order.created_at === 'string' && !order.created_at.endsWith('Z') ? order.created_at + 'Z' : order.created_at).toLocaleString('es-EC', { timeZone: 'America/Guayaquil' })}
                          </p>
                        </div>
                        <div 
                          className="px-3 py-1 rounded-full flex items-center gap-2"
                          style={{ background: `${statusColor}20`, color: statusColor }}
                        >
                          <StatusIcon size={16} />
                          <span className="text-sm font-medium">
                            {statusConfig[order.status].label}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <p className="text-sm flex items-start gap-2">
                          <MapPin size={16} className="mt-1 text-accent-text flex-shrink-0" />
                          <span>{order.customer_address}</span>
                        </p>
                        <p className="text-sm flex items-center gap-2">
                          <Phone size={16} className="text-accent-text" />
                          {order.customer_phone}
                        </p>
                        {order.location_lat && order.location_lng && (
                          <button
                            onClick={() => openMap(order.location_lat, order.location_lng)}
                            className="text-sm flex items-center gap-2 text-accent-text hover:underline"
                          >
                            <Navigation size={16} />
                            Ver ubicación en mapa
                          </button>
                        )}
                      </div>

                      {/* Products */}
                      <div className="mb-4">
                        <p className="text-sm font-semibold mb-2">Productos:</p>
                        <div className="space-y-1">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="text-sm flex justify-between">
                              <span>{item.name} x{item.quantity}</span>
                              <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t mt-2 pt-2 flex justify-between items-center" 
                             style={{ borderColor: 'var(--border-light)' }}>
                          <span className="font-bold">Total:</span>
                          <span className="text-xl font-bold text-accent-text">
                            ${order.total.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-text-muted mt-2">
                          <DollarSign size={14} className="inline" /> Pago contra entrega
                        </p>
                      </div>

                      {/* Status Actions */}
                      <div className="flex gap-2 flex-wrap">
                        {order.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateOrderStatus(order.id, 'preparing')}
                              className="btn-primary text-sm"
                              style={{ padding: '8px 16px', minHeight: 'auto' }}
                            >
                              Marcar en Preparación
                            </button>
                            <button
                              onClick={() => updateOrderStatus(order.id, 'cancelled')}
                              className="px-4 py-2 text-sm rounded-full border border-red-500 text-red-500 hover:bg-red-50"
                            >
                              Cancelar
                            </button>
                          </>
                        )}
                        {order.status === 'preparing' && (
                          <button
                            onClick={() => updateOrderStatus(order.id, 'delivered')}
                            className="btn-primary text-sm"
                            style={{ padding: '8px 16px', minHeight: 'auto' }}
                          >
                            Marcar como Entregado
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersManagement;
